// THE OWNER TRYING THEIR OWN FORM FROM THE APP (2026-09-07, the night it was measured twice).
//
// The shop app's QR screen carries a door, "Sign one yourself and watch it land in your inbox",
// added that afternoon. It opens the shop's own consent form — this app — and the owner signs a
// test. The done screen said "Success! Your waiver has been submitted." and stopped, because it
// was written for a client standing at the counter. For an owner in Instagram's or Facebook's
// in-app browser, with no address bar, that was a dead end, and the only way back they knew was
// the ad: Carl at Old Traditions (9:19pm) and Venom Pit Tattoo co (10:14pm) each tapped the ad
// again within minutes, landed on a blank start form, and filled it in a second time. Chris:
// "is there not a fix for the dupe? is this on us?" — yes, and this is it.
//
// The door adds `?wt_try=1`; this reads it once, keeps it for THIS tab only (sessionStorage — a
// client scanning the QR on their own phone never sees it, and the owner's own phone forgets it
// when the tab closes), and the done screen reads it to show the way back to the app. It is a
// hint about which screen to show and nothing else: the API never sees it, the waiver is saved,
// metered and emailed exactly as any other, and the owner-signature rule still decides whether
// it was a client's.
export const TRY_KEY = "wt_try";

/** The shop app's QR screen — where the owner came from, and where the done screen sends them back. Under Waivers' address
 *  since 2026-10-19 (TW-0369; `/qr` before), at `/waivers/qr-code` since 2026-10-20 (the app's lib/qr-address.ts; `/waivers/qr`
 *  the day before): the app answers both old ones with a permanent redirect here. */
export const APP_QR_URL = "https://app.waivers.tattoo/waivers/qr-code";

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function tabStore(): Store | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Did this tab open from the app's "sign one yourself" door? No storage → no, the client's screen. */
export function isTry(store: Store | null = tabStore()): boolean {
  try {
    return store?.getItem(TRY_KEY) === "1";
  } catch {
    return false;
  }
}

export function setTry(on: boolean, store: Store | null = tabStore()): void {
  try {
    if (!store) return;
    if (on) store.setItem(TRY_KEY, "1");
    else store.removeItem(TRY_KEY);
  } catch {
    /* storage blocked — the done screen is the client's, which is the safe one */
  }
}

/** Read `?wt_try=1` off the URL the app's door opened, once, at start. `?wt_try=0` clears it.
 *
 *  AND TAKE IT OFF THE ADDRESS (2026-09-30). Everest Tattoo & Piercings' own website linked the form WITH
 *  `?wt_try=1`: the owner had copied the address bar after trying their form, so every client who tapped
 *  the site's link signed fine (the API never sees this flag) and then read the owner's done screen. The
 *  flag is a hint for one tab, so once it is kept in the tab's storage it comes off the address with
 *  `history.replaceState`, and an address copied from the bar after that is the plain one. A link already
 *  carrying it still works; `SuccessScreen`'s words are true for anyone since the same day. */
export function readTryFromUrl(
  search: string = typeof window === "undefined" ? "" : window.location.search,
  store: Store | null = tabStore(),
  strip: ((clean: string) => void) | null = defaultStrip
): void {
  try {
    const params = new URLSearchParams(search);
    const v = params.get(TRY_KEY);
    if (v !== "1" && v !== "0") return;
    setTry(v === "1", store);
    params.delete(TRY_KEY);
    const rest = params.toString();
    if (strip) strip(rest ? `?${rest}` : "");
  } catch {
    /* malformed URL — leave it as it stands */
  }
}

/** The address bar without the flag, the path and the hash kept; a browser with no history is left alone. */
function defaultStrip(clean: string): void {
  try {
    if (typeof window === "undefined" || !window.history?.replaceState) return;
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${clean}${window.location.hash}`);
  } catch {
    /* a browser that refuses leaves the address as it stands */
  }
}
